import React, { useState } from 'react';
import { useGlobal } from '@rcg/ui-react';
import { navigate } from '@gatsbyjs/reach-router';
import { globalizationPath } from '../../../../utils/globalization/globalizationPath';
import CabinTabSelector from '../../../CabinTabSelector';
import AirLoader from '../../../AirLoader';
import useDictionary from '../../../../hooks/useDictionary';
import useStyles from './useStyles';
import Dictionary from '../../../../utils/dictionary';
import {
  FLIGHT_SEARCH_TYPE,
  NAVIGATION_PATHS,
  SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR,
  FLIGHT_TYPE_CODES,
} from '../../../../utils/constants';
import { getGuests } from '../../../../utils/payment';
import {
  heldReservationVar,
  shouldSearchReturnResultsVar,
  searchResultsVar,
  bookingProfileVar,
  searchParamsVar,
  selectedDepartureVar,
} from '../../../../operations/localState/reactiveVariables';
import setSearchResults from '../../../../operations/localState/setSearchResults';
import setSelectedDeparture from '../../../../operations/localState/setSelectedDeparture';
import setSelectedReturn from '../../../../operations/localState/setSelectedReturn';
import setHeldReservation from '../../../../operations/localState/setHeldReservation';
import useHoldFlight from '../../../../operations/dataAccess/useHoldFlight';
import useFlightProfileEmail from '../../../../operations/dataAccess/useFlightProfileEmail';
import { setBookingEmail } from '../../../../operations/localState/setBookingProfile';
import { setSessionStorageItem } from '../../../../operations/sessionStorage/utils';

const FlightExpanded = (props) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const {
    data: { lang, market },
  } = useGlobal();
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const { flight, flightType } = props;
  const searchResults = searchResultsVar();
  const [lastSelectedCabinHashKey, setLastSelectedCabinHashKey] = useState(null);

  const initialCabinCode = (() => {
    if (flightType !== FLIGHT_SEARCH_TYPE.RETURN) return null;
    const departureCabinCode = selectedDepartureVar()?.cabin?.cabinClass?.code;
    const groups = flight?.details || [];
    const group = groups.find((g) => g.cabinClass.code === departureCabinCode) || groups[0];
    return group?.cabinClass?.code ?? null;
  })();
  const [selectedCabinCode, setSelectedCabinCode] = useState(initialCabinCode);

  const goToDepartureWithNoFlights = () => {
    setSearchResults(
      {
        flightFilters: null,
        matchingFlights: [],
        flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
      },
      true,
    );
    setSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR, true);
    navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
  };

  const [getBookingEmail, { loading: flightProfileLoading }] = useFlightProfileEmail({
    fetchPolicy: 'no-cache',
    variables: {
      limit: -1,
    },
    onCompleted: (data) => {
      setBookingEmail(data.flightProfile.guests[0].email);
      navigate(`${urlPath}${NAVIGATION_PATHS.reviewFlight}`);
    },
    onError: () => {
      navigate(`${urlPath}${NAVIGATION_PATHS.error}`);
    },
  });

  const [addReservation, { loading: reservationLoading }] = useHoldFlight({
    onCompleted: (data) => {
      setHeldReservation({
        ...data,
        associatedHashKey: lastSelectedCabinHashKey,
      });
      getBookingEmail();
    },
    onError: (error) => {
      if (
        String(error).includes('Re-hold is not allowed') ||
        String(error).includes('ACE-AB-1210')
      ) {
        if (
          heldReservationVar() &&
          heldReservationVar().associatedHashKey === lastSelectedCabinHashKey
        ) {
          navigate(`${urlPath}${NAVIGATION_PATHS.reviewFlight}`);
        } else {
          goToDepartureWithNoFlights();
        }
      } else if (
        String(error).includes('Flight not available') ||
        String(error).includes('ACE-AB-1209')
      ) {
        goToDepartureWithNoFlights();
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.error}`);
      }
    },
  });

  const bookingProfile = bookingProfileVar();

  const selectCabin = (selectedFlight, selectedCabin) => {
    if (flightType === FLIGHT_SEARCH_TYPE.DEPARTURE) {
      setSelectedDeparture({
        flight: selectedFlight,
        cabin: selectedCabin,
      });
      if (bookingProfile?.bookingToken) {
        if (flight?.routeType?.code === FLIGHT_TYPE_CODES.ONE_WAY) {
          setLastSelectedCabinHashKey(selectedCabin.hashKey);
          const guests = getGuests(bookingProfileVar, searchParamsVar);
          addReservation({
            variables: {
              correlationID: searchResults.correlationID,
              hashKey: selectedCabin.hashKey,
              bookingID: bookingProfile?.currentBooking?.id,
              guests,
            },
          });
        } else {
          navigate(`${urlPath}${NAVIGATION_PATHS.resultsReturn}`);
          shouldSearchReturnResultsVar(true);
        }
      } else if (
        !bookingProfile?.bookingToken &&
        flight?.routeType?.code === FLIGHT_TYPE_CODES.ONE_WAY
      ) {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseSummary}`);
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseReturn}`);
      }
    } else {
      setSelectedReturn({
        flight: selectedFlight,
        cabin: selectedCabin,
      });
      setLastSelectedCabinHashKey(selectedCabin.hashKey);
      if (bookingProfile && bookingProfile.bookingToken) {
        const guests = getGuests(bookingProfileVar, searchParamsVar);
        addReservation({
          variables: {
            correlationID: searchResults.correlationID,
            hashKey: selectedCabin.hashKey,
            bookingID: bookingProfile?.currentBooking?.id,
            guests,
          },
        });
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseSummary}`);
      }
    }
  };

  return (
    <>
      <AirLoader
        open={reservationLoading || flightProfileLoading}
        status={
          (reservationLoading || flightProfileLoading) &&
          getDictionary(Dictionary.loaderHoldingFlights.key)
        }
      />
      <div className={classes.cabinSelectorWrapper}>
        <CabinTabSelector
          cabinGroups={flight.details}
          selectedCode={selectedCabinCode}
          selectedHashKey={lastSelectedCabinHashKey}
          onCabinSelect={setSelectedCabinCode}
          onFareSelect={selectCabin}
          flight={flight}
        />
      </div>
    </>
  );
};

export default FlightExpanded;
